import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  BarChart3, TrendingUp, TrendingDown, Minus, AlertTriangle, Eye, ClipboardCheck, 
  GraduationCap, Download, RefreshCw, Target, Shield, FileText, Building2,
  ChevronRight, Calendar, Clock, AlertCircle, CheckCircle2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, RadialBarChart, RadialBar } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExecutiveDashboard = () => {
  const [timeRange, setTimeRange] = useState('month');
  const [overview, setOverview] = useState(null);
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [incidentAnalysis, setIncidentAnalysis] = useState(null);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [departmentComparison, setDepartmentComparison] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    fetchAllData();
  }, [timeRange]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, scoreRes, incidentRes, complianceRes, deptRes, trendsRes] = await Promise.all([
        fetch(`${API_URL}/api/executive-dashboard/overview?time_range=${timeRange}`),
        fetch(`${API_URL}/api/executive-dashboard/safety-score/breakdown`),
        fetch(`${API_URL}/api/executive-dashboard/incidents/analysis?time_range=quarter`),
        fetch(`${API_URL}/api/executive-dashboard/compliance/status`),
        fetch(`${API_URL}/api/executive-dashboard/department-comparison`),
        fetch(`${API_URL}/api/executive-dashboard/trends?months=6`)
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (scoreRes.ok) setScoreBreakdown(await scoreRes.json());
      if (incidentRes.ok) setIncidentAnalysis(await incidentRes.json());
      if (complianceRes.ok) setComplianceStatus(await complianceRes.json());
      if (deptRes.ok) setDepartmentComparison(await deptRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
    } catch (error) {
      toast({ title: "Error", description: "Failed to load dashboard data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      const response = await fetch(`${API_URL}/api/executive-dashboard/export/pdf?time_range=${timeRange}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ include_sections: ['overview', 'incidents', 'compliance', 'departments'] })
      });
      if (response.ok) {
        const data = await response.json();
        toast({ title: "Report Generated", description: `Report ready: ${data.filename}` });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate report", variant: "destructive" });
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const getStatusColor = (status) => {
    const colors = {
      on_track: 'bg-green-100 text-green-800',
      at_risk: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800',
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      needs_improvement: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="executive-dashboard-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Executive Safety Dashboard
          </h1>
          <p className="text-gray-500 mt-1">High-level safety performance overview for leadership</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-40" data-testid="time-range-select">
              <SelectValue placeholder="Time Range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
              <SelectItem value="quarter">Last Quarter</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchAllData} variant="outline" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={exportReport} className="gap-2" data-testid="export-pdf-btn">
            <Download className="w-4 h-4" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Safety Score Hero */}
      {overview && (
        <Card className="bg-gradient-to-r from-blue-600 to-blue-800 text-white" data-testid="safety-score-card">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center">
                    <div className="text-center">
                      <div className="text-4xl font-bold">{overview.safety_score.current}</div>
                      <div className="text-sm opacity-80">Safety Score</div>
                    </div>
                  </div>
                  <Badge className={`absolute -bottom-1 left-1/2 -translate-x-1/2 ${getStatusColor(overview.safety_score.status)}`}>
                    {overview.safety_score.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    {overview.safety_score.trend > 0 ? (
                      <TrendingUp className="w-5 h-5 text-green-300" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-300" />
                    )}
                    <span className="text-lg">{overview.safety_score.trend > 0 ? '+' : ''}{overview.safety_score.trend}% from previous period</span>
                  </div>
                  <div className="text-sm opacity-80">Target: {overview.safety_score.target}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {overview.kpis.slice(0, 4).map((kpi) => (
                  <div key={kpi.id} className="bg-white/10 rounded-lg p-3 min-w-[140px]">
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-bold">{kpi.value}</span>
                      {getTrendIcon(kpi.trend_direction)}
                    </div>
                    <div className="text-sm opacity-80">{kpi.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
          {overview.kpis.map((kpi) => (
            <Card key={kpi.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{kpi.name}</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="text-3xl font-bold">{kpi.value}</span>
                      <span className="text-sm text-gray-400">{kpi.unit}</span>
                    </div>
                  </div>
                  <Badge className={getStatusColor(kpi.status)}>
                    {kpi.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-3 text-sm">
                  {getTrendIcon(kpi.trend_direction)}
                  <span className={kpi.trend > 0 ? 'text-green-600' : kpi.trend < 0 ? 'text-red-600' : 'text-gray-500'}>
                    {kpi.trend > 0 ? '+' : ''}{kpi.trend}%
                  </span>
                  <span className="text-gray-400">vs previous period</span>
                </div>
                {kpi.target && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>Progress to target ({kpi.target})</span>
                      <span>{Math.round((kpi.value / kpi.target) * 100)}%</span>
                    </div>
                    <Progress value={Math.min((kpi.value / kpi.target) * 100, 100)} className="h-1.5" />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="incidents">Incidents</TabsTrigger>
          <TabsTrigger value="compliance">Compliance</TabsTrigger>
          <TabsTrigger value="departments">Departments</TabsTrigger>
          <TabsTrigger value="trends">Trends</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Safety Score Breakdown */}
            {scoreBreakdown && (
              <Card data-testid="score-breakdown-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-600" />
                    Safety Score Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {scoreBreakdown.components.map((component) => (
                      <div key={component.category} className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">{component.category}</span>
                          <span className="text-lg font-bold">{component.score}</span>
                        </div>
                        <Progress value={component.score} className="h-2" />
                        <p className="text-xs text-gray-500">Weight: {component.weight}% | Contribution: {component.weighted_contribution}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Compliance Summary */}
            {complianceStatus && (
              <Card data-testid="compliance-summary-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Compliance Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-24 h-24">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="100%" data={[{ value: complianceStatus.overall_compliance }]}>
                          <RadialBar dataKey="value" fill="#22c55e" />
                        </RadialBarChart>
                      </ResponsiveContainer>
                    </div>
                    <div>
                      <div className="text-3xl font-bold">{complianceStatus.overall_compliance}%</div>
                      <div className="text-sm text-gray-500">Overall Compliance</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {complianceStatus.categories.slice(0, 4).map((cat) => (
                      <div key={cat.name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">{cat.name}</span>
                        <Badge className={cat.compliance >= 90 ? 'bg-green-100 text-green-800' : cat.compliance >= 80 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                          {cat.compliance}%
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          {incidentAnalysis && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Summary Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Incident Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                      <span>Total Incidents</span>
                      <span className="text-2xl font-bold text-red-600">{incidentAnalysis.summary.total_incidents}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="p-3 bg-gray-50 rounded">
                        <div className="text-xl font-bold">{incidentAnalysis.summary.recordable}</div>
                        <div className="text-xs text-gray-500">Recordable</div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded">
                        <div className="text-xl font-bold">{incidentAnalysis.summary.lost_time}</div>
                        <div className="text-xs text-gray-500">Lost Time</div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded">
                        <div className="text-xl font-bold">{incidentAnalysis.summary.first_aid}</div>
                        <div className="text-xs text-gray-500">First Aid</div>
                      </div>
                      <div className="p-3 bg-green-50 rounded">
                        <div className="text-xl font-bold text-green-600">{incidentAnalysis.summary.days_since_last_recordable}</div>
                        <div className="text-xs text-gray-500">Days Safe</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* By Type Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>By Incident Type</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={incidentAnalysis.by_type} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={80} label>
                        {incidentAnalysis.by_type.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Rates */}
              <Card>
                <CardHeader>
                  <CardTitle>Safety Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <div className="text-3xl font-bold text-blue-600">{incidentAnalysis.trends.trir}</div>
                      <div className="text-sm text-gray-500">Total Recordable Incident Rate</div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="text-3xl font-bold text-yellow-600">{incidentAnalysis.trends.dart}</div>
                      <div className="text-sm text-gray-500">DART Rate</div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="text-3xl font-bold text-red-600">{incidentAnalysis.trends.ltir}</div>
                      <div className="text-sm text-gray-500">Lost Time Incident Rate</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-4">
          {complianceStatus && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {complianceStatus.categories.map((category) => (
                  <Card key={category.name}>
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-lg">{category.name}</CardTitle>
                        <Badge className={category.status === 'compliant' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                          {category.compliance}%
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {category.items.map((item, index) => (
                          <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              {item.status === 'current' ? (
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-yellow-500" />
                              )}
                              <span className="text-sm">{item.item}</span>
                            </div>
                            <span className="text-xs text-gray-500">{item.status}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Upcoming Deadlines */}
              <Card data-testid="upcoming-deadlines-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    Upcoming Deadlines
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {complianceStatus.upcoming_deadlines.map((deadline, index) => (
                      <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${deadline.days_remaining < 0 ? 'bg-red-50' : deadline.days_remaining <= 7 ? 'bg-yellow-50' : 'bg-gray-50'}`}>
                        <div>
                          <div className="font-medium">{deadline.item}</div>
                          <div className="text-sm text-gray-500">Due: {deadline.due_date}</div>
                        </div>
                        <Badge className={deadline.days_remaining < 0 ? 'bg-red-100 text-red-800' : deadline.priority === 'high' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>
                          {deadline.days_remaining < 0 ? 'OVERDUE' : `${deadline.days_remaining} days`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Departments Tab */}
        <TabsContent value="departments" className="space-y-4">
          {departmentComparison && (
            <Card data-testid="department-comparison-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  Department Safety Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3">Rank</th>
                        <th className="text-left p-3">Department</th>
                        <th className="text-center p-3">Safety Score</th>
                        <th className="text-center p-3">Incidents</th>
                        <th className="text-center p-3">Incident Rate</th>
                        <th className="text-center p-3">Near Misses</th>
                        <th className="text-center p-3">Training %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departmentComparison.departments.sort((a, b) => a.rank - b.rank).map((dept) => (
                        <tr key={dept.name} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <Badge className={dept.rank === 1 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}>
                              #{dept.rank}
                            </Badge>
                          </td>
                          <td className="p-3 font-medium">{dept.name}</td>
                          <td className="p-3 text-center">
                            <span className={`font-bold ${dept.safety_score >= 80 ? 'text-green-600' : dept.safety_score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {dept.safety_score}
                            </span>
                          </td>
                          <td className="p-3 text-center">{dept.incidents}</td>
                          <td className="p-3 text-center">{dept.incident_rate}</td>
                          <td className="p-3 text-center">{dept.near_misses}</td>
                          <td className="p-3 text-center">{dept.training_completion}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-4">
          {trends && (
            <div className="grid grid-cols-1 gap-4">
              <Card data-testid="trends-chart-card">
                <CardHeader>
                  <CardTitle>Safety Score Trend (6 Months)</CardTitle>
                  <CardDescription>
                    Overall direction: <span className="text-green-600 font-medium">{trends.summary.overall_direction}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trends.trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis domain={[60, 100]} />
                      <Tooltip />
                      <Line type="monotone" dataKey="safety_score" stroke="#3b82f6" strokeWidth={2} name="Safety Score" />
                      <Line type="monotone" dataKey="training_completion" stroke="#22c55e" strokeWidth={2} name="Training %" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Incident Trend</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trends.trends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="incidents" fill="#ef4444" name="Incidents" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExecutiveDashboard;
